'use client'

import { useRouter } from 'next/navigation'
import Logo from '@/components/ui/Logo'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn, scrollToSection } from '@/lib/utils'

const appIconVariants = cva(
  'relative shrink-0 overflow-hidden outline outline-[0.75px] outline-white/0',
  {
    variants: {
      interactive: {
        true: 'cursor-pointer',
      },
      active: {
        true: 'shadow-icon-active outline-primary-500 outline-1',
      },
      invert: {
        true: '',
        false: '',
      },
      circular: {
        true: 'rounded-full',
        false: 'squircle',
      },
      transition: {
        true: 'transition-all duration-300',
        false: '',
      },
    },
    defaultVariants: {
      interactive: false,
      active: false,
      invert: false,
      transition: true,
    },
  },
)

const iconBorderVariants = cva('absolute z-20 h-full w-full', {
  variants: {
    active: {
      true: 'shadow-none',
      false: '',
    },
    invert: {
      true: 'shadow-icon-invert',
      false: 'shadow-icon',
    },
    circular: {
      true: 'rounded-full',
      false: 'squircle',
    },
  },
  defaultVariants: {
    active: false,
    invert: false,
    circular: false,
  },
})

type AppIconProps = Omit<
  React.HTMLAttributes<HTMLDivElement>,
  'onClick' | 'onKeyDown' | 'role' | 'tabIndex'
> &
  VariantProps<typeof appIconVariants> & {
    url?: string | null
    glyph?: any | null
    icon?: any
    name?: string
    href?: string | null
    slug?: string | null
    background?: string | null
    size?: number
    disabled?: boolean
    priority?: boolean
    loading?: 'eager' | 'lazy'
    transition?: boolean
    ignoreScroll?: boolean
    hrefResolver?: (slug: string) => string
    onClick?: React.MouseEventHandler<HTMLDivElement>
    onActivate?: (e: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>) => void
    onNavigate?: (href: string) => void
  }

const defaultHrefResolver = (slug: string) => `/apps/${slug}`

export default function AppIcon({
  url,
  glyph,
  icon,
  name,
  href,
  slug,
  background,
  size,
  active,
  invert,
  circular = false,
  onClick,
  className,
  disabled,
  style,
  hrefResolver = defaultHrefResolver,
  onNavigate,
  onActivate,
  priority,
  loading,
  transition = true,
  ignoreScroll = false,
  ...rest
}: AppIconProps) {
  const router = useRouter()
  const resolvedBackground = background ?? undefined
  const resolvedHref: string | undefined = href ?? (slug ? hrefResolver(slug) : undefined)

  // Determine which icon to use - prefer glyph if it's a valid Media object, otherwise use icon
  const resolvedIcon = glyph && typeof glyph === 'object' && glyph !== null ? glyph : icon
  const shouldRenderLogo = Boolean(resolvedIcon || url)

  // Extract glyph positioning properties
  const hasGlyphPositioning =
    glyph &&
    typeof glyph === 'object' &&
    (glyph.coveragePercent !== undefined ||
      glyph.xOffsetPercent !== undefined ||
      glyph.yOffsetPercent !== undefined)

  const hasOffset =
    glyph &&
    ((glyph.xOffsetPercent !== undefined && glyph.xOffsetPercent !== 0) ||
      (glyph.yOffsetPercent !== undefined && glyph.yOffsetPercent !== 0))

  const glyphStyle = hasGlyphPositioning
    ? {
        position: 'relative',
        width: glyph.coveragePercent ? `${glyph.coveragePercent}%` : '100%',
        height: glyph.coveragePercent ? `${glyph.coveragePercent}%` : '100%',
        ...(hasOffset && {
          transform: `translate(${glyph.xOffsetPercent || 0}%, ${glyph.yOffsetPercent || 0}%)`,
        }),
      }
    : { width: '100%', height: '100%', position: 'relative' }

  const isInteractive = !disabled && !!(onClick || onActivate || resolvedHref)

  const handleActivate = () => {
    if (!resolvedHref) return

    if (slug && !ignoreScroll) {
      const target = document.getElementById(slug)
      if (target) {
        scrollToSection({ id: slug, offset: 150, animate: true, hideHash: true })
        return
      }
    }

    if (onNavigate) {
      onNavigate(resolvedHref)
    } else {
      router.push(resolvedHref)
    }
  }

  const handleClick = disabled
    ? undefined
    : (e: React.MouseEvent<HTMLDivElement>) => {
        handleActivate()
        onActivate?.(e)
        onClick?.(e)
      }

  const handleKeyDown = disabled
    ? undefined
    : (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key !== 'Enter' && e.key !== ' ') return
        e.preventDefault()
        handleActivate()
        onActivate?.(e)
      }

  return (
    <div
      role={isInteractive ? 'button' : undefined}
      tabIndex={isInteractive ? 0 : undefined}
      {...rest}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className={cn(
        appIconVariants({
          active,
          invert,
          circular,
          interactive: isInteractive,
          transition,
        }),
        className,
      )}
      style={{
        backgroundColor: resolvedBackground,
        width: size ? `${size}px` : `100%`,
        height: size ? `${size}px` : `100%`,
        ...style,
      }}
    >
      <div className={cn(background && iconBorderVariants({ active, invert, circular }))}></div>
      <div className="absolute inset-0 flex items-center justify-center">
        <div style={glyphStyle}>
          {shouldRenderLogo ? (
            <Logo
              url={url}
              icon={resolvedIcon}
              alt={name ?? 'Efficient App'}
              size={size ?? undefined}
              fill={size ? false : true}
              priority={priority}
              loading={loading}
            />
          ) : null}
        </div>
      </div>
    </div>
  )
}
